/* =============================================================
   useStudentList.js — Student list state with Supabase persistence
   -------------------------------------------------------------
   Fetches incoming or outgoing students from the Supabase
   "students" table and exposes addStudent / addStudents to
   insert new rows.

   Usage:
     const { students, loading, error, addStudent, addStudents }
       = useStudentList('incoming');

     const { students, loading, error, addStudent, addStudents }
       = useStudentList('outgoing');

   Data flow:
     1. On mount, fetches all students of the given type from
        Supabase (filtered by university via RLS automatically).
     2. Each DB row is mapped to the shape IncomingList /
        OutgoingList expect (see toUIShape below).
     3. Documents are fetched in the same query as an embedded
        relation — Supabase resolves them via the foreign key
        on documents.student_id. The docs status ('Complete',
        'Pending', etc.) is computed from them.
     4. addStudent / addStudents INSERT into Supabase and then
        refetch the list so the UI shows the real DB row.

   TAB-FREEZE RESILIENCE:
     AbortController + 8-second timeout on every fetch.
     visibilitychange listener refetches when the tab regains focus.

   ✓ DONE (backend integration):
   - Removed BASE_INCOMING / BASE_OUTGOING mock seed data
   - Removed localStorage persistence
   - Replaced with Supabase SELECT + INSERT
   - Documents status computed from embedded documents relation
   - university_id taken from AuthContext (no hardcoding)
   ============================================================= */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase }                                  from '../lib/supabase';
import { useAuth }                                   from '../context/AuthContext';
import { resolveProcessTemplate } from '../lib/studentCategory';

const FETCH_TIMEOUT_MS = 8000;

/* ─── computeNextDeadline ────────────────────────────────────── */
// Given a student's current step index and the per-step deadline arrays,
// returns the earliest upcoming deadline from the current step onwards.
// stepDeadlines: array of arrays — stepDeadlines[i] = [date_str, ...]
function computeNextDeadline(stepIdx, stepDeadlines) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let nearest = null;
  for (let i = stepIdx; i < stepDeadlines.length; i++) {
    for (const dl of (stepDeadlines[i] ?? [])) {
      if (!dl) continue;
      const d = new Date(dl + 'T00:00:00');
      if (d >= today && (!nearest || d < nearest)) nearest = d;
    }
  }
  return nearest
    ? nearest.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
}


/* ─── computeDocsStatus ──────────────────────────────────────── */
function computeDocsStatus(documents) {
  if (!documents || documents.length === 0) return 'Not started';

  const rejected = documents.filter(d => d.review_status === 'rejected').length;
  const pending  = documents.filter(d => d.review_status === 'pending').length;

  if (rejected > 0) return `${rejected} missing`;
  if (pending  > 0) return 'Pending';
  return 'Complete';
}


/* ─── toUIShape ──────────────────────────────────────────────── */
// processMap:      { [studentId]: current_step_index }
// templateStepMap: { [templateId]: { labels: [], deadlines: [] } }
// templates:       array of university_process_templates
function toUIShape(row, type, processMap = {}, templateStepMap = {}, templates = []) {
  const docs    = computeDocsStatus(row.documents ?? []);
  const stepIdx = processMap[row.id] ?? 0;

  // Resolve which template this student uses
  // semester_id on the template is used inside resolveProcessTemplate for filtering
  const template     = resolveProcessTemplate(templates, row);
  const templateData = template ? (templateStepMap[template.id] ?? {}) : {};
  const stepLabels   = templateData.labels   ?? [];
  const stepDeadlines = templateData.deadlines ?? [];

  // Status = current process step label (falls back to students.status
  // if no steps are configured for this university).
  const status      = stepLabels.length > 0
    ? (stepLabels[stepIdx] ?? row.status)
    : row.status;
  const isCompleted = stepLabels.length > 0
    && stepIdx >= stepLabels.length - 1;

  // Next deadline: earliest upcoming deadline from current step onwards
  const deadline = computeNextDeadline(stepIdx, stepDeadlines);

  if (type === 'incoming') {
    return {
      id:         row.id,
      name:       row.name,
      email:      row.email ?? null,
      uni:        row.home_university ?? '—',
      country:    row.country,
      semesterId: row.semester_id ?? null,
      status,
      isCompleted,
      docs,
      deadline,
    };
  }

  return {
    id:         row.id,
    name:       row.name,
    email:      row.email ?? null,
    dest:       row.destination_university ?? '—',
    country:    row.country,
    semesterId: row.semester_id ?? null,
    status,
    isCompleted,
    docs,
    la:         row.learning_agreement_status ?? 'Not started',
    deadline,
    urgent:     row.is_urgent ?? false,
  };
}


/* ─── useStudentList HOOK ────────────────────────────────────── */
export function useStudentList(type) {
  const { user } = useAuth();

  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Tracks the current in-flight AbortController so we can cancel it
  // when a new fetch starts or the component unmounts.
  const abortRef = useRef(null);


  /* ── fetchStudents ─────────────────────────────────────────── */
  const fetchStudents = useCallback(async () => {
    // Cancel any previous in-flight request.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // Hard timeout: clears the spinner even if Chrome froze the fetch.
    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setError(null);

    try {
      // Three parallel queries: students, process state, templates
      const [studentsRes, processRes, templatesRes] = await Promise.all([
        supabase
          .from('students')
          .select('*, documents(review_status)')
          .eq('type', type)
          .eq('archived', false)
          .order('created_at', { ascending: false })
          .abortSignal(signal),
        supabase
          .from('student_process')
          .select('student_id, current_step')
          .abortSignal(signal),
        user?.universityId
          ? supabase
              .from('university_process_templates')
              .select('id, semester_id, match_degree, match_categories, is_default, process_steps(label, step_order, process_step_documents(deadline_date))')
              .eq('university_id', user.universityId)
              .eq('type', type)
              .order('created_at', { referencedTable: 'process_steps', ascending: true })
              .abortSignal(signal)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (signal.aborted) return;

      if (studentsRes.error) {
        setError(studentsRes.error.message);
        return;
      }

      // Build processMap: { studentId → current_step_index }
      const processMap = {};
      for (const row of (processRes.data ?? [])) {
        processMap[row.student_id] = row.current_step;
      }

      // Build templateStepMap: { templateId → { labels, deadlines } }
      const templates = templatesRes.data ?? [];
      const templateStepMap = {};
      for (const t of templates) {
        const sortedSteps = (t.process_steps ?? []).sort((a, b) => a.step_order - b.step_order);
        templateStepMap[t.id] = {
          labels:    sortedSteps.map(s => s.label).filter(Boolean),
          deadlines: sortedSteps.map(s =>
            (s.process_step_documents ?? []).map(psd => psd.deadline_date).filter(Boolean)
          ),
        };
      }

      setStudents((studentsRes.data ?? []).map(row =>
        toUIShape(row, type, processMap, templateStepMap, templates)
      ));
    } catch (err) {
      if (signal.aborted) return;
      setError(err?.message ?? 'Failed to load students');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [type, user?.universityId]); // eslint-disable-line


  /* ── initial load ──────────────────────────────────────────── */
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  /* ── Refetch when the user switches back to this tab ─────────── */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchStudents();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchStudents]);

  /* ── Real-time subscription ──────────────────────────────────── */
  // Re-fetches the list whenever any student row is inserted, updated,
  // or deleted for this university — keeps all IO managers in sync.
  useEffect(() => {
    if (!user?.universityId) return;

    const channel = supabase
      .channel(`students_${type}_${user.universityId}`)
      .on(
        'postgres_changes',
        {
          event:  '*',
          schema: 'public',
          table:  'students',
          filter: `university_id=eq.${user.universityId}`,
        },
        () => fetchStudents()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.universityId, type, fetchStudents]);

  /* ── Abort on unmount ────────────────────────────────────────── */
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);


  /* ── addStudent ────────────────────────────────────────────── */
  const addStudent = async (formData) => {
    if (!user?.universityId) throw new Error('Not authenticated');

    const row = {
      university_id:          user.universityId,
      type,
      name:                   formData.name.trim(),
      email:                  formData.email?.trim().toLowerCase() ?? null,
      nationality:            formData.nationality ?? null,
      // country kept for backward-compat (column may be NOT NULL) — use nationality
      country:                formData.nationality ?? 'Unknown',
      eu_status:              formData.euStatus    ?? null,
      exchange_type:          formData.exchangeType ?? null,
      degree_level:           formData.degreeLevel  ?? null,
      degree_level_custom:    formData.degreeLevelCustom ?? null,
      home_university:        type === 'incoming' ? (formData.uni  ?? null) : null,
      destination_university: type === 'outgoing' ? (formData.dest ?? null) : null,
      partner_id:             formData.partnerId ?? null,
      // status kept for backward-compat (column may be NOT NULL) — default to Nominated
      status:                 'Nominated',
      semester_id:            formData.semesterId   ?? null,
      archived:               false,
    };

    const { error: insertError } = await supabase.from('students').insert(row);
    if (insertError) throw insertError;

    await fetchStudents();
  };


  /* ── addStudents ───────────────────────────────────────────── */
  const addStudents = async (dataArray) => {
    if (!user?.universityId) throw new Error('Not authenticated');

    const rows = dataArray.map(formData => ({
      university_id:          user.universityId,
      type,
      name:                   formData.name.trim(),
      email:                  formData.email?.trim().toLowerCase() ?? null,
      nationality:            formData.nationality ?? null,
      country:                formData.nationality ?? 'Unknown',
      eu_status:              formData.euStatus    ?? null,
      exchange_type:          formData.exchangeType ?? null,
      degree_level:           formData.degreeLevel  ?? null,
      degree_level_custom:    formData.degreeLevelCustom ?? null,
      home_university:        type === 'incoming' ? (formData.uni  ?? null) : null,
      destination_university: type === 'outgoing' ? (formData.dest ?? null) : null,
      partner_id:             formData.partnerId ?? null,
      status:                 'Nominated',
      semester_id:            formData.semesterId   ?? null,
      archived:               false,
    }));

    const { error: insertError } = await supabase.from('students').insert(rows);
    if (insertError) throw insertError;

    await fetchStudents();
  };


  /* ── updateStudent ────────────────────────────────────────── */
  const updateStudent = async (id, fields) => {
    // fields: { name, email, home_university, destination_university, country }
    const { error: updateError } = await supabase
      .from('students')
      .update(fields)
      .eq('id', id);

    if (updateError) throw updateError;

    await fetchStudents();
  };

  /* ── deleteStudent ─────────────────────────────────────────── */
  const deleteStudent = async (id) => {
    const { error: deleteError } = await supabase
      .from('students')
      .delete()
      .eq('id', id);

    if (deleteError) throw deleteError;

    localStorage.removeItem(`unixplore_computed_status_incoming_${id}`);
    localStorage.removeItem(`unixplore_computed_status_outgoing_${id}`);

    await fetchStudents();
  };

  /* ── deleteAllStudents ─────────────────────────────────────── */
  const deleteAllStudents = async () => {
    if (!user?.universityId) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('students')
      .delete()
      .eq('university_id', user.universityId)
      .eq('type', type);

    if (deleteError) throw deleteError;

    await fetchStudents();
  };


  return { students, loading, error, addStudent, addStudents, updateStudent, deleteStudent, deleteAllStudents };
}
